"use client";

import { useCheckout, PaymentElement } from "@stripe/react-stripe-js/checkout";
import { CheckoutPromotionCode } from "@/components/checkout-promo-code";
import { usePriceFormatter } from "@/lib/hooks/use-price-formatter";
import { Button } from "@/components/catalyst/button";
import { InfoAlert } from "@/components/info-alert";
import { Creator, Product } from "@/lib/entities";
import { useSession } from "@/lib/auth/client";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils/classes";
import { Text } from "./catalyst/text";
import { useState } from "react";

type CheckoutFormProps = {
  product: Product;
  creator: Creator;
  disabled?: boolean;
};

export function CheckoutForm({
  product,
  creator,
  disabled,
}: CheckoutFormProps) {
  const t = useTranslations("CheckoutForm");
  const formatPrice = usePriceFormatter();
  const checkoutState = useCheckout();

  const { data: session } = useSession();

  const [isLoading, setIsLoading] = useState(false);
  const [price, setPrice] = useState(product.price);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  if (checkoutState.type === "error") {
    return <InfoAlert variant="error" message={checkoutState.error.message} />;
  }

  if (checkoutState.type === "loading") {
    return <div>{t("loading")}</div>;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const email = session?.user?.email;
    const { checkout } = checkoutState;

    if (email) {
      await checkout.updateEmail(email);
    }

    const confirmResult = await checkout.confirm();

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (confirmResult.type === "error") {
      setMessage({ type: "error", text: confirmResult.error.message });
    }

    setIsLoading(false);
  };

  const handlePromoSuccess = (message: string) => {
    setMessage({ type: "success", text: message });
  };

  const handlePromoError = (message: string) => {
    setMessage({ type: "error", text: message });
  };

  const isButtonDisabled =
    isLoading || creator.userId === session?.user?.id || disabled;

  const isFree = price === 0;
  const formattedPrice = formatPrice(price, product.currency);

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-4">
        <PaymentElement id="payment-element" />

        <CheckoutPromotionCode
          isDisabled={isButtonDisabled}
          originalPrice={product.price}
          onChange={setPrice}
          onSuccess={handlePromoSuccess}
          onError={handlePromoError}
        />

        {message && (
          <div
            className={cn(
              "rounded-md border p-3",
              message.type === "success"
                ? "border-green-200 bg-green-50 text-green-800"
                : "border-red-200 bg-red-50 text-red-800",
            )}
          >
            <p className="text-sm">{message.text}</p>
          </div>
        )}

        <div className="pt-4">
          <Button
            type="submit"
            disabled={isButtonDisabled}
            className="w-full"
            color="purple"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="mr-2 h-5 w-5 animate-spin rounded-full border-b-2 border-white"></div>
                {t("processing")}
              </div>
            ) : isFree ? (
              formattedPrice
            ) : (
              t("payNow", { price: formattedPrice })
            )}
          </Button>
        </div>
      </form>

      <Text className="text-center text-xs/6 text-zinc-500 sm:text-xs/6 dark:text-zinc-400">
        {t("paymentInfo")
          .split("\n")
          .map((line, i, arr) => (
            <span key={i}>
              {line}
              {i < arr.length - 1 && <br />}
            </span>
          ))}
      </Text>
    </div>
  );
}
